import { AllocationDimension } from '@apis/Invoices/model';
import { Query } from '@apis/Invoices/model/query';
import { QueryExpr, QueryResult } from '@apis/Resources';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Center, Divider, Group, Loader, Text } from '@mantine/core';
import { DataGrid, gridCurrencyCellRenderer } from '@root/Components/DataGrid';
import { DataGridModel } from '@root/Components/DataGrid/DataGridModel';
import { ColumnConfig, ColumnGroupConfig, DataGridState, ISelectionStrategy } from '@root/Components/DataGrid/Models';
import { useDi, useDiContainer, useDiMemo } from '@root/Services/DI';
import { EventEmitter, useEvent, useEventValue } from '@root/Services/EventEmitter';
import { FormatService, NamedFormats } from '@root/Services/FormatService';
import { InvoiceApiService } from '@root/Services/Invoices/InvoiceApiService';
import { ShowbackPersistence } from '@root/Services/Invoices/ShowbackService';
import { ArrayDataSource } from '@root/Services/Query/ArrayDataSource';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronRight } from 'tabler-icons-react';
import { inject, injectable } from 'tsyringe';
import { ShowbackModel } from '../Models';
import { UserPreferenceService } from '@root/Services/Customers/UserPreferenceService';
import { ValuesGroupOtherText } from '@root/Services/QueryExpr';
import { EmptyDataText } from '@root/Components/Text/VisibleSpaces';
import { ShowbackSummaryModel } from '../Summary/ShowbackSummaryModel';

export type ReconciliationGridRow = {
    assetId?: string;
    invoiceAmount?: number;
    showbackAmount?: number;
    showbackAmortizedAmount?: number;
    untaggedCostsAdjustments?: number;
    savingsPlans?: number;
    reservedInstances?: number;
    edp?: number;
    bundledDiscount?: number;
    privateRateDiscount?: number;
    credits?: number;
    awsnonRIRelated?: number;
    userAdded?: number;
    marketplace?: number;
    supportAllocation?: number;
    otherUsage?: number;
    internal?: number;
    amortizationSavingsPlans?: number;
    amortizationReservedInstances?: number;
    amortizationOther?: number;
};

@injectable()
export class ReconciliationGridModel {
    public onFilterChanged?: (filters: QueryExpr[]) => void;
    public baseDataSourceAllocated!: ReconciliationGridRow[];
    public baseDataSourceUnallocated!: ReconciliationGridRow[];
    public allocatedDataSource!: ArrayDataSource;
    public unallocatedDataSource!: ArrayDataSource;
    public datasource!: ReconciliationGridRow[];
    public datasourceTotal!: ReconciliationGridRow[];
    public datasourceUnallocated!: ReconciliationGridRow[];
    public datasourceUnallocatedTotal!: ReconciliationGridRow[];
    public totalData!: ReconciliationGridRow[];
    public gridModel?: DataGridModel;
    public sortDatasource?: (state: DataGridState) => void;
    public columns!: ColumnConfig<ReconciliationGridRow>[];
    public parentGroup!: { label: string; value: string }[];
    public queryApi!: (query: Query) => Promise<QueryResult<any>>;
    public initializing = new EventEmitter(true);
    public dataRefreshNeeded = EventEmitter.empty();
    public groupConfig: { [groupName: string]: ColumnGroupConfig } = {
        Reconciliation: { color: '#ECFDF3', noOverlay: true },
        Discounts: { color: '#E6E2F1', noOverlay: true },
        Credits: { color: '#F9E6E2', noOverlay: true },
        Fees: { color: '#E8F8F5', noOverlay: true },
        Marketplace: { color: '#EAF2F8', noOverlay: true },
        ['Shared Costs']: { color: '#D2F2DD', noOverlay: true },
        ['Untagged Costs']: { color: '#D2F2DD', noOverlay: true },
        //Charges: { color: '#FCE4D3', noOverlay: true },
        Amortization: { color: '#BCC7D8', noOverlay: true },
        // Other: { color: '#BCC7D8', noOverlay: true },
    };
    public month?: Date;
    public invalidateDatasource?: () => void;
    public allocDimName?: string;
    private moneyFormat?: string;

    public constructor(
        @inject(FormatService) private readonly formatSvc: FormatService,
        @inject(ShowbackPersistence) private readonly showbackPersistence: ShowbackPersistence,
        @inject(InvoiceApiService) private readonly invoiceApi: InvoiceApiService,
        @inject(UserPreferenceService) private userPrefSvc: UserPreferenceService
    ) {}

    public init(month: Date, allocDim: AllocationDimension, state?: DataGridState) {
        this.month = month;
        this.queryApi = (query: Query) => this.invoiceApi.queryByUsageMonth(query, month);
        this.initialize(allocDim, state);
        return this;
    }

    public async initialize(allocDim: AllocationDimension, state?: DataGridState) {
        try {
            this.initializing.emit(true);
            this.allocDimName = this.showbackPersistence.getDimensionName(allocDim);
            this.baseDataSourceAllocated = await this.createDataSource(this.month!, allocDim);
            this.moneyFormat = this.getFormat();
            const secondaryColumns = this.createSecondaryColumns();
            this.updateData(this.baseDataSourceAllocated, this.baseDataSourceUnallocated);
            this.columns = [
                ...this.createBaseColumns(),
                ...secondaryColumns.filter((c) => {
                    const accessor = c.accessor as keyof ReconciliationGridRow;
                    return !this.datasource.every((row) => (row[accessor] ?? 0) === 0);
                }),
            ];
            for (const column of this.columns) {
                column.noReorder = true;
                column.noRemove = true;
            }
        } finally {
            this.initializing.emit(false);
        }
    }

    private getFormat(): NamedFormats {
        const userPrefFmt = this.userPrefSvc.get('ShowbackUserPrefs', { format: 'money' as NamedFormats }).format;
        return userPrefFmt === 'money'
            ? 'number-with-two-decimals'
            : userPrefFmt === 'money-whole'
            ? 'int'
            : userPrefFmt === 'money-with-two-decimals'
            ? 'number-with-two-decimals'
            : userPrefFmt === 'money-with-four-decimals'
            ? 'number-with-four-decimals'
            : userPrefFmt === 'money-with-six-decimals'
            ? 'number-with-six-decimals'
            : userPrefFmt === 'money-with-seven-decimals'
            ? 'number-with-seven-decimals'
            : 'int';
    }

    public updateData(allocDataSource?: ReconciliationGridRow[], unallocDataSource?: ReconciliationGridRow[]) {
        this.datasource = allocDataSource ?? this.baseDataSourceAllocated;
        //this.datasourceUnallocated = unallocDataSource ?? this.baseDataSourceUnallocated;
        this.datasourceTotal = this.getDataSourceTotal(true);
        //this.datasourceUnallocatedTotal = this.getDataSourceTotal(false);
        //this.totalData = this.getAllTotal();
        this.dataRefreshNeeded.emit();
    }

    public setOnFilterChanged(handler: ((filters: QueryExpr[]) => void) | undefined) {
        if (handler !== this.onFilterChanged) {
            this.onFilterChanged = handler;
        }
        return this;
    }

    private async createDataSource(month: Date, allocDim: AllocationDimension) {
        const { adjusted, billed, full, orig } = await this.showbackPersistence.getAllocatedData(allocDim, month);
        const toLookup: <T extends { assetId: string }>(data: T[]) => Map<string, T> = (data) =>
            data.reduce((lookup, item) => lookup.set(item.assetId, item), new Map());
        const adjCostLookup = toLookup(adjusted);
        const billedCostLookup = toLookup(billed);
        type AdjField = keyof (typeof adjusted)[number];
        const adjFields = [...new Set([adjusted, billed].flatMap((d) => Object.keys(d[0] ?? {})))] as AdjField[];
        const getAdjAmt = (assetId: string, field: Exclude<AdjField, 'assetId'>) =>
            (adjCostLookup.get(assetId)?.[field] ?? 0) - (billedCostLookup.get(assetId)?.[field] ?? 0);
        const createAdjRow = (assetId: string) =>
            adjFields.reduce(
                (row, field) => {
                    if (field !== 'assetId') {
                        row[field] = getAdjAmt(assetId, field);
                    }
                    return row;
                },
                { assetId } as Record<Exclude<AdjField, 'assetId'>, number> & { assetId: string }
            );

        const fullLookup = toLookup(full);
        const origLookup = toLookup(orig);
        const getInvounceAmt =
            origLookup.size <= 1
                ? (assetId: string) => fullLookup.get(assetId)?.invoiceAmount ?? 0
                : (assetId: string) => origLookup.get(assetId)?.invoiceAmount ?? 0;

        const assetIds = [...new Set(...[fullLookup, adjCostLookup, billedCostLookup, origLookup].map((l) => l.keys()))];

        const data = [...assetIds].map((assetId) => {
            const full = fullLookup.get(assetId);
            return {
                ...fullLookup.get(assetId),
                invoiceAmount: getInvounceAmt(assetId),
                untaggedCostsAdjustments: (full?.untaggedCost ?? 0) + (full?.adjustedTagCost ?? 0),
                ...createAdjRow(assetId),
            } as ReconciliationGridRow;
        });

        return data ?? [];
    }

    private getDataSourceTotal(isAllocated: boolean) {
        const data = isAllocated ? this.datasource : this.datasourceUnallocated;
        if (data.length === 0) {
            return [];
        }
        const valuePropertyKeys = Object.keys(data[0]) as (keyof ReconciliationGridRow)[];

        const initialTotals = valuePropertyKeys.reduce((acc, key) => ({ ...acc, [key]: 0 }), {}) as Record<string, number>;
        const total = data.reduce((sum, current) => {
            valuePropertyKeys.forEach((key) => {
                if (typeof current[key] === 'number') {
                    if (!sum[key]) {
                        sum[key] = 0;
                    }
                    sum[key] += (current[key] as number) ?? 0;
                }
            });
            return sum;
        }, initialTotals);
        const totalRow = total as ReconciliationGridRow;
        totalRow.assetId = 'Total';
        return [totalRow];
    }

    private createBaseColumns() {
        const result: ColumnConfig<ReconciliationGridRow>[] = [
            {
                accessor: (r) => (r.assetId === ValuesGroupOtherText ? null : r.assetId),
                header: `${this.allocDimName}`,
                defaultWidth: 200,
                id: 'assetId',
                type: 'string',
                sortField: 'assetId',
                defaultFixed: true,
                filter: {
                    filterType: 'string',
                    name: this.allocDimName ?? '',
                    filterField: 'assetId',
                    options: {
                        getValueProvider: () =>
                            this.baseDataSourceAllocated
                                .filter((r) => r.assetId !== ValuesGroupOtherText)
                                .map((r) => r.assetId ?? '')
                                .map((v) => ({ value: v, label: v })),
                    },
                },
                cellRenderer: (r) => (r.assetId === ValuesGroupOtherText ? <EmptyDataText text="« Unallocated »" /> : <>{r.assetId}</>),
            },
            {
                header: 'Invoice Amount',
                accessor: 'invoiceAmount',
                defaultWidth: 150,
                id: 'invoiceAmount',
                type: 'number',
                align: 'right',
                sortField: 'invoiceAmount',
                defaultFixed: true,
                headerRenderer: () => (
                    <div style={{ lineHeight: '25px', height: 50 }}>
                        Invoice <br /> Amount
                    </div>
                ),
                cellRenderer: gridCurrencyCellRenderer((v) => v.invoiceAmount ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Reconciliation',
                filter: {
                    filterField: 'invoiceAmount',
                    name: 'Invoice Amount',
                    filterType: 'number',
                    fieldPickerRenderer: () => 'Invoice Amount',
                },
            },
            {
                header: 'Showback Amount',
                accessor: 'showbackAmount',
                defaultWidth: 150,
                id: 'showbackAmount',
                type: 'number',
                align: 'right',
                sortField: 'showbackAmount',
                defaultFixed: true,
                headerRenderer: () => (
                    <div style={{ lineHeight: '25px' }}>
                        Showback <br /> Amount
                    </div>
                ),
                cellRenderer: gridCurrencyCellRenderer((v) => v.showbackAmount ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Reconciliation',
                filter: {
                    filterField: 'showbackAmount',
                    name: 'Showback Amount',
                    filterType: 'number',
                    fieldPickerRenderer: () => 'Showback Amount',
                },
            },
            {
                header: 'Showback (Amortized) Amount',
                accessor: 'showbackAmortizedAmount',
                defaultWidth: 160,
                id: 'showbackAmortizedAmount',
                type: 'number',
                align: 'right',
                sortField: 'showbackAmortizedAmount',
                defaultFixed: true,
                headerRenderer: () => (
                    <div style={{ lineHeight: '25px' }}>
                        Showback
                        <br />
                        (Amortized) Amount
                    </div>
                ),
                cellRenderer: gridCurrencyCellRenderer((v) => v.showbackAmortizedAmount ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Reconciliation',
                filter: {
                    filterField: 'showbackAmortizedAmount',
                    name: 'Showback (Amortized) Amount',
                    filterType: 'number',
                    fieldPickerRenderer: () => 'Showback (Amortized) Amount',
                },
            },
        ];

        return result;
    }

    private createSecondaryColumns() {
        const otherColumns: ColumnConfig<ReconciliationGridRow>[] = [
            {
                header: 'Untagged Costs: Adjustments',
                accessor: 'untaggedCostsAdjustments',
                defaultWidth: 150,
                id: 'untaggedCostsAdjustments',
                type: 'number',
                align: 'right',
                sortField: 'untaggedCostsAdjustments',
                headerRenderer: () => (
                    <div style={{ lineHeight: '25px' }}>
                        <br /> Adjustments
                    </div>
                ),
                cellRenderer: gridCurrencyCellRenderer((v) => v.untaggedCostsAdjustments ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Untagged Costs',
                filter: true,
            },
            {
                header: 'Savings Plans',
                accessor: 'savingsPlans',
                defaultWidth: 150,
                id: 'savingsPlans',
                type: 'number',
                align: 'right',
                sortField: 'savingsPlans',
                cellRenderer: gridCurrencyCellRenderer((v) => v.savingsPlans ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Discounts',
                filter: true,
            },
            {
                header: 'Reservations',
                accessor: 'reservedInstances',
                defaultWidth: 150,
                id: 'reservedInstances',
                type: 'number',
                align: 'right',
                sortField: 'reservedInstances',
                cellRenderer: gridCurrencyCellRenderer((v) => v.reservedInstances ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Discounts',
                filter: {
                    filterField: 'reservedInstances',
                    name: 'Reservations',
                    filterType: 'number',
                },
            },
            {
                header: 'EDP / PPA',
                accessor: 'edp',
                defaultWidth: 150,
                id: 'edp',
                type: 'number',
                align: 'right',
                sortField: 'edp',
                cellRenderer: gridCurrencyCellRenderer((v) => v.edp ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Discounts',
                filter: true,
            },
            {
                header: 'Bundled Discount',
                accessor: 'bundledDiscount',
                defaultWidth: 150,
                id: 'bundledDiscount',
                type: 'number',
                align: 'right',
                sortField: 'bundledDiscount',
                cellRenderer: gridCurrencyCellRenderer((v) => v.bundledDiscount ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Discounts',
                filter: true,
            },
            {
                header: 'Private Rate Discount',
                accessor: 'privateRateDiscount',
                defaultWidth: 150,
                id: 'privateRateDiscount',
                type: 'number',
                align: 'right',
                sortField: 'privateRateDiscount',
                headerRenderer: () => (
                    <div style={{ lineHeight: '25px' }}>
                        Private Rate <br /> Discount
                    </div>
                ),
                cellRenderer: gridCurrencyCellRenderer((v) => v.privateRateDiscount ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Discounts',
                filter: true,
            },
            {
                header: 'Credits',
                accessor: 'credits',
                defaultWidth: 150,
                id: 'credits',
                type: 'number',
                align: 'right',
                sortField: 'credits',
                cellRenderer: gridCurrencyCellRenderer((v) => v.credits ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Credits',
                filter: true,
            },
            {
                header: 'Fees',
                accessor: 'awsnonRIRelated',
                defaultWidth: 150,
                id: 'awsnonRIRelated',
                type: 'number',
                align: 'right',
                sortField: 'awsnonRIRelated',
                headerRenderer: () => (
                    <div style={{ lineHeight: '25px' }}>
                        AWS Non-RI <br /> Related
                    </div>
                ),
                cellRenderer: gridCurrencyCellRenderer((v) => v.awsnonRIRelated ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Fees',
                filter: true,
            },
            {
                header: 'User-Added',
                accessor: 'userAdded',
                defaultWidth: 150,
                id: 'userAdded',
                type: 'number',
                align: 'right',
                sortField: 'userAdded',
                cellRenderer: gridCurrencyCellRenderer((v) => v.userAdded ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Fees',
                filter: true,
            },
            {
                header: 'Marketplace',
                accessor: 'marketplace',
                defaultWidth: 150,
                id: 'marketplace',
                type: 'number',
                align: 'right',
                sortField: 'marketplace',
                cellRenderer: gridCurrencyCellRenderer((v) => v.marketplace ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Marketplace',
                filter: true,
            },
            {
                header: 'Support Allocation',
                accessor: 'supportAllocation',
                defaultWidth: 150,
                id: 'supportAllocation',
                type: 'number',
                align: 'right',
                sortField: 'supportAllocation',
                headerRenderer: () => (
                    <div style={{ lineHeight: '25px' }}>
                        Support <br />
                        Allocation
                    </div>
                ),
                cellRenderer: gridCurrencyCellRenderer((v) => v.supportAllocation ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Shared Costs',
                filter: true,
            },
            {
                header: 'Other Usage',
                accessor: 'otherUsage',
                defaultWidth: 150,
                id: 'otherUsage',
                type: 'number',
                align: 'right',
                sortField: 'otherUsage',
                cellRenderer: gridCurrencyCellRenderer((v) => v.otherUsage ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Shared Costs',
                filter: true,
            },
            {
                header: 'Savings Plans - Up Front Payments',
                accessor: 'amortizationSavingsPlans',
                defaultWidth: 150,
                id: 'amortizationSavingsPlans',
                type: 'number',
                align: 'right',
                sortField: 'amortizationSavingsPlans',
                headerRenderer: () => (
                    <div style={{ lineHeight: '25px' }}>
                        Savings Plans <br /> Up Front Payments
                    </div>
                ),
                cellRenderer: gridCurrencyCellRenderer((v) => v.amortizationSavingsPlans ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Amortization',
                filter: true,
            },
            {
                header: 'Reserved Instances - Up Front Payments',
                accessor: 'amortizationReservedInstances',
                defaultWidth: 150,
                id: 'amortizationReservedInstances',
                type: 'number',
                align: 'right',
                sortField: 'amortizationReservedInstances',
                headerRenderer: () => (
                    <div style={{ lineHeight: '25px' }}>
                        Reserved Instances <br /> Up Front Payments
                    </div>
                ),
                cellRenderer: gridCurrencyCellRenderer((v) => v.amortizationReservedInstances ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Amortization',
                filter: {
                    filterField: 'amortizationReservedInstances',
                    name: 'Reserved Instances',
                    filterType: 'number',
                    fieldPickerRenderer: () => 'Reserved Instances',
                },
            },
            {
                header: 'Amortization: Other',
                accessor: 'amortizationOther',
                defaultWidth: 150,
                id: 'amortizationOther',
                type: 'number',
                align: 'right',
                sortField: 'amortizationOther',
                headerRenderer: () => (
                    <div style={{ lineHeight: '25px' }}>
                        Amortization <br /> Other
                    </div>
                ),
                cellRenderer: gridCurrencyCellRenderer((v) => v.amortizationOther ?? 0, this.moneyFormat ?? 'money'),
                groupName: 'Amortization',
                filter: true,
            },
        ];
        return otherColumns;
    }

    public attach = (gridModel: DataGridModel) => {
        this.gridModel = gridModel;
        this.updateData();

        gridModel.gridStateChanged.listen((change) => {
            this.allocatedDataSource ??= gridModel.createArrayDataSource(this.baseDataSourceAllocated);
            this.unallocatedDataSource ??= gridModel.createArrayDataSource(this.baseDataSourceUnallocated);
            const updatedAllocDataSource = this.allocatedDataSource.applyState(change?.state ?? gridModel.gridState);
            const updatedUnallocDataSource = this.unallocatedDataSource.applyState(change?.state ?? gridModel.gridState);
            this.updateData(updatedAllocDataSource, updatedUnallocDataSource);
        });
    };

    public handleStateLoaded = () => {
        this.invalidateDatasource?.();
        this.onFilterChanged?.((this.gridModel?.gridState.filters ?? []).slice() as QueryExpr[]);
    };
}

export function ReconciliationGrid({
    onFilterChanged,
    persistenceKey,
    selectionStrategy,
    model,
}: {
    onFilterChanged?: (filters: QueryExpr[]) => void;
    persistenceKey: string;
    selectionStrategy?: ISelectionStrategy<ReconciliationGridRow>;
    model: ShowbackModel;
}) {
    const summaryModel = useDiMemo(ShowbackSummaryModel, [model], (m) => m.setSbModel(model).init());
    const theme = useTheme();
    const container = useDiContainer();
    const selectedMonth = useEventValue(model.selectedMonthChanged)!;
    const [dataCount, setDataCount] = useState<ReactNode>('');
    const fmtSvc = useDi(FormatService);
    const moneyFormat = useEventValue(model.moneyFormatChanged);
    const [gridState, setGridState] = useState<DataGridState>();
    const gridModel = useMemo(() => {
        const reconciliationGridModel = container
            .resolve(ReconciliationGridModel)
            .init(selectedMonth, model.selectedAllocationDimension!, gridState)
            .setOnFilterChanged(onFilterChanged);
        return reconciliationGridModel;
    }, [selectedMonth, moneyFormat]);

    useEffect(() => {
        gridModel.setOnFilterChanged(onFilterChanged);
    }, [onFilterChanged]);
    const initializing = useEventValue(gridModel.initializing);
    const [allocatedGridCollapsed, setAllocatedGridCollapsed] = useState(false);
    //const [unallocatedGridCollapsed, setUnallocatedGridCollapsed] = useState(false);
    const getCollapseStyle = (collapsed: boolean, maxHeight?: number) => ({
        background: '#fff',
        transition: 'max-height 400ms',
        maxHeight: collapsed ? 0 : maxHeight ?? '100%',
    });

    //const toggleAllocated = useCallback(() => setAllocatedGridCollapsed((v) => !v), [setAllocatedGridCollapsed]);
    //const toggleUnallocated = useCallback(() => setUnallocatedGridCollapsed((v) => !v), [setUnallocatedGridCollapsed]);
    useEffect(() => {
        const count = gridModel.datasource?.length;
        setDataCount(
            <Text data-atid={'DataGridResults:' + count?.toString()} color="dimmed">
                {fmtSvc.formatInt(count ?? 0)} result{count == 1 ? '' : 's'}
            </Text>
        );
    }, [gridModel.datasource]);
    useEvent(gridModel.dataRefreshNeeded);

    return initializing ? (
        <Center>
            <Loader />
        </Center>
    ) : (
        <DataGrid
            key={persistenceKey}
            columns={gridModel.columns}
            dataSource={[]}
            headerHeight={50}
            groupConfig={gridModel.groupConfig}
            onModelLoaded={gridModel.attach}
            minimumLoadingMs={0}
            showCount={false}
            leftResultsPlaceHolder={dataCount}
            showHeaderGroups
            hideColumnSelector
            splitBodyProps={[
                {
                    datasource: gridModel.datasourceTotal,
                    columns: gridModel.columns,
                    height: 31,
                    style: { background: theme.colors.gray[0], borderBottom: 'none', fontWeight: 'bold' },
                },
                {
                    datasource: gridModel.datasource,
                    columns: gridModel.columns,
                    scrollable: true,
                },
            ]}
        />
    );
}

function GridSectionCollapser({
    label,
    collapsed,
    onClick,
    noBorder,
}: {
    label: string;
    collapsed: boolean;
    onClick: () => void;
    noBorder?: boolean;
}) {
    return (
        <GridSectionCollapserEl noBorder={!!noBorder} onClick={onClick}>
            <ChevronRight size={20} style={{ transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)', transition: 'all 400ms' }} />
            <Text span weight="600">
                {label}
            </Text>
        </GridSectionCollapserEl>
    );
}

const GridSectionCollapserEl = styled.a<{ noBorder: boolean }>`
    cursor: pointer;
    display: flex;
    height: 30px;
    align-items: center;
    padding: 0 16px;
    gap: 8px;
    background: ${(p) => p.theme.colors.primary[2]};
    border-color: ${(p) => p.theme.colors.gray[4]};
    border-style: solid;
    border-width: 0 ${(p) => (p.noBorder ? 0 : '1px')};
    &:hover {
        background: ${(p) => p.theme.colors.primary[1]};
    }
`;
